import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  LinearProgress,
  IconButton,
  InputAdornment,
  ListItemButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import { tokens } from '../../global/theme/tokens';
import { API_GET, useConfig } from '../../api/api';
import { formatBytes } from '../exo/explorer/MediaInfo';
import {
  decodeSfmName,
  entryKind,
  entryLabel,
  listingToEntries,
  parentPath,
  pathPrefixes,
} from '../exo/project-file-explorer/explorerUtils';
import { request } from './aiChatApi';

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DELAY = 400;

/**
 * Picks files from a project's network storage folder. Files there have no
 * File id, they are referenced by project and path.
 */
export const AiProjectFilePicker = ({
  open,
  onClose,
  projectId,
  projectLabel,
  maxItems,
  onSelect,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [project, setProject] = useState(null);
  const [path, setPath] = useState('');
  const [listing, setListing] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selected, setSelected] = useState({}); // path -> entry
  const requestSeqRef = useRef(0);

  const trimmedQuery = query.trim();
  const searching = trimmedQuery.length >= SEARCH_MIN_LENGTH;
  const noStorage = project && !project.networkStorage;

  useEffect(() => {
    if (!open) return;
    setPath('');
    setQuery('');
    setSelected({});
    setProject(null);
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !config || !projectId) return;
    request(config, `projects/${projectId}`, API_GET)
      .then((res) => setProject(res.data))
      .catch(() => setProject(null));
  }, [open, config, projectId]);

  // folder listing, or a project wide search while a query is typed
  useEffect(() => {
    if (!open || !config || !projectId || noStorage) return undefined;
    const seq = ++requestSeqRef.current;
    const apiPath = searching
      ? `projects/${projectId}/files/search?path=&query=${encodeURIComponent(trimmedQuery)}`
      : `projects/${projectId}/files?path=${encodeURIComponent(path)}&sortBy=name&sortOrder=asc`;

    setIsLoading(true);
    setLoadError(false);
    const handle = setTimeout(
      () =>
        request(config, apiPath, API_GET)
          .then((res) => {
            if (requestSeqRef.current !== seq) return;
            if (searching) setSearchResults(res.data);
            else setListing(res.data);
          })
          .catch(() => {
            if (requestSeqRef.current === seq) setLoadError(true);
          })
          .finally(() => {
            if (requestSeqRef.current === seq) setIsLoading(false);
          }),
      searching ? SEARCH_DELAY : 0,
    );
    return () => clearTimeout(handle);
  }, [open, config, projectId, noStorage, path, searching, trimmedQuery]);

  const entries = listingToEntries(searching ? searchResults : listing);
  const selectedCount = Object.keys(selected).length;
  const limitReached = selectedCount >= maxItems;

  const openFolder = (folderPath) => {
    setQuery('');
    setPath(folderPath);
  };

  const toggleFile = (entry) =>
    setSelected((prev) => {
      if (prev[entry.path]) {
        const { [entry.path]: removed, ...rest } = prev;
        return rest;
      }
      if (Object.keys(prev).length >= maxItems) return prev;
      return { ...prev, [entry.path]: entry };
    });

  const handleAttach = () => {
    onSelect(
      Object.values(selected).map((entry) => ({
        projectId: Number(projectId),
        path: entry.path,
        name: entryLabel(entry),
        mimeType: entry.mimeType,
      })),
    );
  };

  const rootLabel = project?.name || projectLabel || t('Project files');
  const crumbs = pathPrefixes(path);

  return (
    <Dialog
      open={Boolean(open)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.glass,
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          height: '75vh',
        },
      }}
    >
      <Box className="flex items-center gap-2 px-4 pt-3 pb-2">
        <Box className="flex flex-col min-w-0 flex-1">
          <Typography variant="h6" fontWeight={600}>
            {t('From project folder')}
          </Typography>
          <Typography variant="caption" noWrap sx={{ opacity: 0.6 }}>
            {rootLabel}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ opacity: 0.6 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {noStorage ? (
        <Box className="flex-1 flex flex-col items-center justify-center gap-2 opacity-60 px-6 text-center">
          <CloudOffIcon sx={{ fontSize: 40 }} />
          <Typography variant="body2">
            {t('This project has no network storage configured.')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box className="px-4 pb-2">
            <TextField
              fullWidth
              size="small"
              autoFocus
              placeholder={t('Search in project folder...')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, opacity: 0.5 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: colors.bgInput,
                  borderRadius: '10px',
                  '& fieldset': { border: 'none' },
                },
              }}
            />
          </Box>

          {!searching && (
            <Box className="flex items-center flex-wrap gap-0.5 px-4 pb-1">
              {crumbs.map((crumb, index) => (
                <Box key={crumb || 'root'} className="flex items-center min-w-0">
                  {index > 0 && (
                    <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.4 }} />
                  )}
                  <Typography
                    variant="caption"
                    noWrap
                    onClick={() => openFolder(crumb)}
                    className="cursor-pointer rounded px-1"
                    sx={{
                      maxWidth: 180,
                      fontWeight: crumb === path ? 600 : 400,
                      opacity: crumb === path ? 1 : 0.6,
                      '&:hover': { backgroundColor: colors.bgInput, opacity: 1 },
                    }}
                  >
                    {index === 0
                      ? rootLabel
                      : decodeSfmName(crumb.split('/').pop())}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <LinearProgress
            sx={{
              height: 2,
              mx: 2,
              visibility: isLoading && entries.length > 0 ? 'visible' : 'hidden',
            }}
          />
          <Box className="flex-1 min-h-0 overflow-y-auto px-2 pb-2">
            {isLoading && entries.length === 0 ? (
              <Box className="flex justify-center py-10">
                <CircularProgress size={24} />
              </Box>
            ) : loadError ? (
              <Typography
                variant="body2"
                className="text-center py-10"
                sx={{ color: colors.error }}
              >
                {t('The project folder could not be loaded.')}
              </Typography>
            ) : entries.length === 0 ? (
              <Box className="flex flex-col items-center gap-1 py-10 opacity-50">
                <FolderOffIcon />
                <Typography variant="body2">
                  {searching ? t('No results found') : t('This folder is empty')}
                </Typography>
              </Box>
            ) : (
              entries.map((entry) => (
                <EntryRow
                  key={entry.path}
                  entry={entry}
                  showLocation={searching}
                  checked={Boolean(selected[entry.path])}
                  disabled={
                    !entry.isDir && limitReached && !selected[entry.path]
                  }
                  onClick={() =>
                    entry.isDir ? openFolder(entry.path) : toggleFile(entry)
                  }
                />
              ))
            )}
          </Box>
        </>
      )}

      <Box
        className="flex items-center gap-2 px-4 py-3"
        sx={{ borderTop: `1px solid ${colors.bgInput}` }}
      >
        <Typography variant="caption" sx={{ flex: 1, opacity: 0.7 }}>
          {limitReached
            ? t('Maximum of {{count}} files reached', { count: maxItems })
            : t('{{count}} selected', { count: selectedCount })}
        </Typography>
        <Button color="secondary" onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="info"
          disableElevation
          disabled={selectedCount === 0}
          onClick={handleAttach}
          sx={{ textTransform: 'none' }}
        >
          {t('Attach')}
        </Button>
      </Box>
    </Dialog>
  );
};

const EntryRow = ({ entry, showLocation, checked, disabled, onClick }) => {
  const { t } = useTranslation();
  const kind = entryKind(entry);
  const location = parentPath(entry.path);

  const details = [
    showLocation && (location ? decodeSfmName(location) : t('Project files')),
    !entry.isDir && entry.size != null && formatBytes(entry.size),
  ].filter(Boolean);

  return (
    <ListItemButton
      onClick={onClick}
      disabled={disabled}
      sx={{ borderRadius: '10px', gap: 1, py: 0.5, px: 1 }}
    >
      <Box sx={{ width: 30 }} className="flex justify-center flex-shrink-0">
        {!entry.isDir && (
          <Checkbox
            size="small"
            checked={checked}
            tabIndex={-1}
            disableRipple
            sx={{ p: 0.5 }}
          />
        )}
      </Box>
      <kind.Icon sx={{ fontSize: 22, color: kind.color, flexShrink: 0 }} />
      <Box className="flex flex-col min-w-0 flex-1">
        <Typography variant="body2" noWrap fontWeight={500}>
          {entryLabel(entry)}
        </Typography>
        {details.length > 0 && (
          <Typography variant="caption" noWrap sx={{ opacity: 0.55 }}>
            {details.join(' · ')}
          </Typography>
        )}
      </Box>
      {entry.isDir && <ChevronRightIcon sx={{ opacity: 0.4 }} />}
    </ListItemButton>
  );
};
